import { UserProfile, UserStats, GameSession, UnlockedAchievement } from '../types/profile';
import { ACHIEVEMENTS } from '../data/achievements';
import { supabase } from '../lib/supabase';

interface OfflineProfile extends UserProfile {
  isOfflineProfile: boolean;
  pendingGameSessions: GameSession[];
  lastSyncAt?: string;
}

interface SyncStatus {
  isOnline: boolean;
  lastSyncAt?: string;
  pendingSessionsCount: number;
}

export class OfflineProfileService {
  private static readonly PROFILE_KEY = 'banana_offline_profile';
  private static readonly SYNC_STATUS_KEY = 'banana_sync_status';
  
  // Get or create offline profile
  static getOrCreateOfflineProfile(userId?: string): OfflineProfile {
    const stored = localStorage.getItem(this.PROFILE_KEY);
    
    if (stored) {
      try {
        const profile = JSON.parse(stored) as OfflineProfile;
        console.log('Loaded offline profile:', { id: profile.id, username: profile.username, pendingSessions: profile.pendingGameSessions.length, achievements: profile.achievements.length });
        
        // Check if this is a transition from guest to authenticated user
        const isTransitioningToAuth = userId && profile.id !== userId && profile.id.startsWith('offline_');
        
        // Update user ID if provided (for when user signs in)
        if (userId && profile.id !== userId) {
          console.log('Updating profile user ID from', profile.id, 'to', userId);
          
          // If transitioning from guest to authenticated, reset achievements
          if (isTransitioningToAuth) {
            console.log('🔄 Transitioning from guest to authenticated user - clearing guest achievements');
            profile.achievements = [];
          }
          
          profile.id = userId;
          this.saveOfflineProfile(profile);
        }
        
        // Only validate achievements if NOT transitioning from guest to auth
        // This prevents awarding achievements based on guest data to new authenticated users
        if (!isTransitioningToAuth) {
          this.validateAchievements(profile);
        }
        
        return profile;
      } catch (error) {
        console.error('Error parsing stored profile:', error);
      }
    }
    
    // Create new offline profile
    const now = new Date().toISOString();
    const newProfile: OfflineProfile = {
      id: userId || `offline_${Date.now()}`,
      username: userId ? 'Player' : 'Offline Player',
      avatar: '🍌',
      createdAt: now,
      lastActiveAt: now,
      stats: this.createEmptyStats(),
      achievements: [],
      preferences: {
        favoriteGameMode: 'classic',
        preferredDifficulty: 'medium',
        soundEnabled: true,
        animationsEnabled: true
      },
      isOfflineProfile: true,
      pendingGameSessions: [],
      lastSyncAt: undefined
    };
    
    console.log('Created new offline profile:', { id: newProfile.id, username: newProfile.username });
    this.saveOfflineProfile(newProfile);
    return newProfile;
  }
  
  // Save offline profile to localStorage
  static saveOfflineProfile(profile: OfflineProfile): void {
    try {
      localStorage.setItem(this.PROFILE_KEY, JSON.stringify(profile));
      this.updateSyncStatus({
        isOnline: navigator.onLine,
        pendingSessionsCount: profile.pendingGameSessions.length
      });
    } catch (error) {
      console.error('Error saving offline profile:', error);
    }
  }
  
  // Add game session to offline profile
  static addGameSessionToOfflineProfile(gameSession: GameSession, userId?: string): UnlockedAchievement[] {
    const profile = this.getOrCreateOfflineProfile(userId);
    
    // Store old stats before updating (deep copy)
    const oldStats: UserStats = {
      ...profile.stats,
      measurementStats: {
        length: { ...profile.stats.measurementStats.length },
        weight: { ...profile.stats.measurementStats.weight },
        volume: { ...profile.stats.measurementStats.volume }
      },
      gameModeStats: { ...profile.stats.gameModeStats }
    };
    
    // Add to pending sessions
    profile.pendingGameSessions.push(gameSession);
    
    // Update stats locally
    profile.stats = this.updateStatsWithSession(profile.stats, gameSession);
    profile.lastActiveAt = new Date().toISOString();
    
    // Check for new achievements using OLD stats vs NEW stats
    const newAchievements = this.checkAchievements(profile, gameSession, oldStats);
    
    // Add new achievements to profile
    newAchievements.forEach(achievement => {
      if (!profile.achievements.find(a => a.id === achievement.id)) {
        profile.achievements.push(achievement);
      }
    });
    
    this.saveOfflineProfile(profile);
    return newAchievements;
  }
  
  // Update profile preferences
  static updateOfflineProfilePreferences(updates: any): void {
    const profile = this.getOrCreateOfflineProfile();
    profile.preferences = { ...profile.preferences, ...updates };
    this.saveOfflineProfile(profile);
  }
  
  // Update profile info
  static updateOfflineProfile(updates: { username?: string; avatar?: string }): void {
    const profile = this.getOrCreateOfflineProfile();
    if (updates.username) profile.username = updates.username;
    if (updates.avatar) profile.avatar = updates.avatar;
    this.saveOfflineProfile(profile);
  }
  
  // Main sync method - simple and clean
  static async syncWithSupabase(userId: string): Promise<{ success: boolean; error?: string }> {
    console.log('🔄 Starting achievement sync...');
    
    try {
      await this.syncAchievementsToDatabase(userId);
      console.log('✅ Sync completed successfully');
      return { success: true };
    } catch (error: any) {
      console.error('❌ Sync failed:', error);
      return { 
        success: false, 
        error: error.message || 'Unknown sync error' 
      };
    }
  }
  
  // Get sync status
  static getSyncStatus(): SyncStatus {
    const stored = localStorage.getItem(this.SYNC_STATUS_KEY);
    const profile = this.getOrCreateOfflineProfile();
    
    const defaultStatus: SyncStatus = {
      isOnline: navigator.onLine,
      pendingSessionsCount: profile.pendingGameSessions.length
    };
    
    if (!stored) return defaultStatus;
    
    try {
      const status = JSON.parse(stored) as SyncStatus;
      return {
        ...status,
        isOnline: navigator.onLine,
        pendingSessionsCount: profile.pendingGameSessions.length
      };
    } catch {
      return defaultStatus;
    }
  }
  
  // Update sync status
  private static updateSyncStatus(updates: Partial<SyncStatus>): void {
    const current = this.getSyncStatus();
    const updated = { ...current, ...updates };
    localStorage.setItem(this.SYNC_STATUS_KEY, JSON.stringify(updated));
  }
  
  // Update offline profile with new user ID (when user signs in)
  static updateOfflineProfileUserId(newUserId: string): void {
    const profile = this.getOrCreateOfflineProfile();
    if (profile.id !== newUserId) {
      profile.id = newUserId;
      // Update username if it was the default offline username
      if (profile.username === 'Offline Player') {
        profile.username = 'Player';
      }
      this.saveOfflineProfile(profile);
    }
  }
  
  // Clear offline data (for testing or reset)
  static clearOfflineData(): void {
    localStorage.removeItem(this.PROFILE_KEY);
    localStorage.removeItem(this.SYNC_STATUS_KEY);
  }

  // Clear all app data including guest achievements (useful for testing)
  static clearAllAppData(): void {
    // Clear offline profile data
    localStorage.removeItem(this.PROFILE_KEY);
    localStorage.removeItem(this.SYNC_STATUS_KEY);
    
    // Clear any other banana game related localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('banana_') || key === 'audioEnabled') {
        localStorage.removeItem(key);
      }
    });
    
    console.log('🧹 Cleared all app data from localStorage');
  }

  // Reset offline profile for new authenticated user (prevents guest data contamination)
  static resetForNewAuthenticatedUser(userId: string): OfflineProfile {
    console.log('🔄 Resetting offline profile for new authenticated user:', userId);
    
    // Clear existing offline data
    this.clearOfflineData();
    
    // Create fresh profile for the authenticated user
    const now = new Date().toISOString();
    const newProfile: OfflineProfile = {
      id: userId,
      username: 'Player',
      avatar: '🍌',
      createdAt: now,
      lastActiveAt: now,
      stats: this.createEmptyStats(),
      achievements: [], // Start fresh with no achievements
      preferences: {
        favoriteGameMode: 'classic',
        preferredDifficulty: 'medium',
        soundEnabled: true,
        animationsEnabled: true
      },
      isOfflineProfile: true,
      pendingGameSessions: [],
      lastSyncAt: undefined
    };
    
    this.saveOfflineProfile(newProfile);
    console.log('✅ Created fresh profile for authenticated user');
    return newProfile;
  }

  // Simple achievement sync - push local achievements to database
  private static async syncAchievementsToDatabase(userId: string): Promise<void> {
    console.log('🔄 Syncing achievements to database...');
    
    try {
      // Get local achievements
      const offlineProfile = this.getOrCreateOfflineProfile(userId);
      const localAchievements = offlineProfile.achievements;
      
      console.log(`Found ${localAchievements.length} local achievements to sync`);
      
      if (localAchievements.length === 0) {
        console.log('No achievements to sync');
        return;
      }
      
      // Get existing achievements from database
      console.log('Checking existing database achievements...');
      const { data: existingAchievements, error: fetchError } = await supabase
        .from('user_achievements')
        .select('achievement_id')
        .eq('user_id', userId);
        
      if (fetchError) {
        console.error('Error fetching existing achievements:', fetchError);
        throw fetchError;
      }
      
      const existingIds = new Set(existingAchievements?.map(a => a.achievement_id) || []);
      console.log('Existing achievement IDs in database:', Array.from(existingIds));
      
      // Find achievements that need to be added
      const achievementsToAdd = localAchievements.filter(achievement => !existingIds.has(achievement.id));
      
      console.log(`Found ${achievementsToAdd.length} new achievements to add:`, achievementsToAdd.map(a => a.id));
      
      if (achievementsToAdd.length === 0) {
        console.log('✅ All achievements already exist in database - sync complete');
        return;
      }
      
      // Insert only the missing achievements
      const achievementsToInsert = achievementsToAdd.map(achievement => ({
        user_id: userId,
        achievement_id: achievement.id,
        unlocked_at: achievement.unlockedAt
      }));
      
      console.log('Inserting missing achievements:', achievementsToInsert);
      
      const { data: _data, error: insertError } = await supabase
        .from('user_achievements')
        .insert(achievementsToInsert);
        
      if (insertError) {
        console.error('Error inserting achievements:', insertError);
        throw insertError;
      }
      
      console.log(`✅ Successfully added ${achievementsToAdd.length} new achievements to database`);
      
    } catch (error) {
      console.error('Achievement sync failed:', error);
      throw error;
    }
  }

  // Helper: Create empty stats
  private static createEmptyStats(): UserStats {
    return {
      totalGames: 0,
      totalScore: 0,
      bestScore: 0,
      longestStreak: 0,
      currentStreak: 0,
      gamesWon: 0,
      totalTimePlayed: 0,
      averageAccuracy: 0,
      measurementStats: {
        length: { gamesPlayed: 0, questionsAnswered: 0, correctAnswers: 0, totalScore: 0, averageAccuracy: 0, bestStreak: 0 },
        weight: { gamesPlayed: 0, questionsAnswered: 0, correctAnswers: 0, totalScore: 0, averageAccuracy: 0, bestStreak: 0 },
        volume: { gamesPlayed: 0, questionsAnswered: 0, correctAnswers: 0, totalScore: 0, averageAccuracy: 0, bestStreak: 0 }
      },
      gameModeStats: {}
    };
  }

  // Helper: Update stats with new game session
  private static updateStatsWithSession(stats: UserStats, session: GameSession): UserStats {
    const accuracy = session.questionsAnswered > 0 ? (session.correctAnswers / session.questionsAnswered) * 100 : 0;
    const isWin = accuracy >= 70; // Consider 70%+ accuracy a win
    
    const newStats: UserStats = {
      ...stats,
      totalGames: stats.totalGames + 1,
      totalScore: stats.totalScore + session.score,
      bestScore: Math.max(stats.bestScore, session.score),
      longestStreak: Math.max(stats.longestStreak, session.streak),
      gamesWon: stats.gamesWon + (isWin ? 1 : 0),
      totalTimePlayed: stats.totalTimePlayed + session.timePlayed,
      averageAccuracy: ((stats.averageAccuracy * stats.totalGames) + accuracy) / (stats.totalGames + 1)
    };
    
    // Update measurement stats
    if (session.measurementType && newStats.measurementStats[session.measurementType]) {
      const measurementStat = newStats.measurementStats[session.measurementType];
      measurementStat.gamesPlayed++;
      measurementStat.questionsAnswered += session.questionsAnswered;
      measurementStat.correctAnswers += session.correctAnswers;
      measurementStat.totalScore += session.score;
      measurementStat.averageAccuracy = (measurementStat.correctAnswers / measurementStat.questionsAnswered) * 100;
      measurementStat.bestStreak = Math.max(measurementStat.bestStreak, session.streak);
    }
    
    // Update game mode stats
    if (!newStats.gameModeStats[session.gameMode]) {
      newStats.gameModeStats[session.gameMode] = {
        gamesPlayed: 0,
        bestScore: 0,
        averageScore: 0,
        totalTimePlayed: 0
      };
    }
    
    const gameModeStat = newStats.gameModeStats[session.gameMode];
    gameModeStat.gamesPlayed++;
    gameModeStat.bestScore = Math.max(gameModeStat.bestScore, session.score);
    gameModeStat.averageScore = ((gameModeStat.averageScore * (gameModeStat.gamesPlayed - 1)) + session.score) / gameModeStat.gamesPlayed;
    gameModeStat.totalTimePlayed += session.timePlayed;
    
    return newStats;
  }

  // Helper: Check for new achievements
  private static checkAchievements(profile: OfflineProfile, session: GameSession, oldStats: UserStats): UnlockedAchievement[] {
    const newAchievements: UnlockedAchievement[] = [];
    const unlockedIds = new Set(profile.achievements.map(a => a.id));
    
    for (const achievement of ACHIEVEMENTS) {
      if (unlockedIds.has(achievement.id)) continue;
      
      // Check if the achievement was just earned this session
      const wasAlreadyEarned = this.checkAchievementCondition(achievement, oldStats, session);
      const isNowEarned = this.checkAchievementCondition(achievement, profile.stats, session);
      
      // Only award if it wasn't earned before but is earned now
      if (!wasAlreadyEarned && isNowEarned) {
        newAchievements.push({
          id: achievement.id,
          unlockedAt: new Date().toISOString()
        });
      }
    }
    
    return newAchievements;
  }

  // Helper: Check if achievement condition is met
  private static checkAchievementCondition(achievement: any, stats: UserStats, session: GameSession): boolean {
    const { requirement } = achievement;
    
    switch (requirement.type) {
      case 'games_played':
        return stats.totalGames >= requirement.value;
      
      case 'total_score':
        if (achievement.id === 'high_scorer') {
          return session.score >= requirement.value;
        }
        return stats.totalScore >= requirement.value;
      
      case 'streak':
        return Math.max(stats.longestStreak, session.streak) >= requirement.value;
      
      case 'measurement_games':
        if (requirement.measurementType && 
            (requirement.measurementType === 'length' || 
             requirement.measurementType === 'weight' || 
             requirement.measurementType === 'volume')) {
          const measurementType = requirement.measurementType as 'length' | 'weight' | 'volume';
          return stats.measurementStats[measurementType].gamesPlayed >= requirement.value;
        }
        if (achievement.id === 'measurement_master') {
          return Object.values(stats.measurementStats).every(stat => stat.gamesPlayed >= 15);
        }
        return false;
      
      case 'game_mode_games':
        if (achievement.id === 'mode_explorer') {
          return Object.keys(stats.gameModeStats).length >= requirement.value;
        }
        return Object.keys(stats.gameModeStats).length >= requirement.value;
      
      case 'perfect_questions':
        // For perfect questions, we check if the current session shows high performance
        // indicating perfect answers. A perfect question gives 100 points.
        // If someone scored high (close to max possible), they likely got perfect answers
        const sessionAccuracy = session.questionsAnswered > 0 ? 
          (session.correctAnswers / session.questionsAnswered) * 100 : 0;
        
        // If they got 100% accuracy in this session, they definitely got perfect questions
        if (achievement.id === 'first_perfect' && sessionAccuracy >= 100) {
          return true;
        }
        
        // For multiple perfect questions, estimate based on high scores and accuracy
        // Perfect score per question is typically 100 points
        const perfectQuestionsThisSession = sessionAccuracy >= 100 ? session.correctAnswers : 0;
        const estimatedTotalPerfects = Math.floor(stats.totalScore / 100) + perfectQuestionsThisSession;
        return estimatedTotalPerfects >= requirement.value;
      
      default:
        return false;
    }
  }

  // Helper: Validate and fix achievements based on current stats
  private static validateAchievements(profile: OfflineProfile): void {
    const currentAchievementIds = new Set(profile.achievements.map(a => a.id));
    let hasChanges = false;
    
    // Check each achievement to see if it should be unlocked based on current stats
    for (const achievement of ACHIEVEMENTS) {
      const shouldBeUnlocked = this.checkAchievementConditionStatic(achievement, profile.stats);
      const isCurrentlyUnlocked = currentAchievementIds.has(achievement.id);
      
      // If it should be unlocked but isn't, add it
      if (shouldBeUnlocked && !isCurrentlyUnlocked) {
        console.log(`Adding missing achievement: ${achievement.name}`);
        profile.achievements.push({
          id: achievement.id,
          unlockedAt: new Date().toISOString()
        });
        hasChanges = true;
      }
    }
    
    // Save profile if we made changes
    if (hasChanges) {
      console.log(`Updated profile with ${profile.achievements.length} total achievements`);
      this.saveOfflineProfile(profile);
    }
  }

  // Helper: Check achievement condition based on stats only (static check)
  private static checkAchievementConditionStatic(achievement: any, stats: UserStats): boolean {
    const { requirement } = achievement;
    
    switch (requirement.type) {
      case 'games_played':
        return stats.totalGames >= requirement.value;
      
      case 'total_score':
        if (achievement.id === 'high_scorer') {
          return false; // Can't determine single-game high score from cumulative stats
        }
        return stats.totalScore >= requirement.value;
      
      case 'streak':
        return stats.longestStreak >= requirement.value;
      
      case 'measurement_games':
        if (requirement.measurementType && 
            (requirement.measurementType === 'length' || 
             requirement.measurementType === 'weight' || 
             requirement.measurementType === 'volume')) {
          const measurementType = requirement.measurementType as 'length' | 'weight' | 'volume';
          return stats.measurementStats[measurementType].gamesPlayed >= requirement.value;
        }
        if (achievement.id === 'measurement_master') {
          return Object.values(stats.measurementStats).every(stat => stat.gamesPlayed >= 15);
        }
        return false;
      
      case 'game_mode_games':
        if (achievement.id === 'mode_explorer') {
          return Object.keys(stats.gameModeStats).length >= requirement.value;
        }
        return Object.keys(stats.gameModeStats).length >= requirement.value;
      
      case 'perfect_questions':
        // For static validation, estimate perfect questions based on high total score
        // Perfect score per question is typically 100 points, so estimate based on total score
        const estimatedPerfects = Math.floor(stats.totalScore / 100);
        return estimatedPerfects >= requirement.value;
      
      default:
        return false;
    }
  }
}

// Make testing methods available globally in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).BananaTestUtils = {
    clearAllData: () => OfflineProfileService.clearAllAppData(),
    getProfile: () => OfflineProfileService.getOrCreateOfflineProfile(),
    resetForAuth: (userId: string) => OfflineProfileService.resetForNewAuthenticatedUser(userId)
  };
} 